import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import useFetch from '../../useFetch';
import { getStoredUser } from '../../auth';
import { apiRequest } from '../../utils/api';
import FormFeedback, { FieldError, inputErrorClass } from '../Common/FormFeedback';
import EmptyState from '../Common/EmptyState';

export default function ApplyJob() {
  const { id } = useParams();
  const user = getStoredUser();

  const [form, setForm] = useState({
    name: user?.name ?? '',
    email: user?.email ?? '',
    phone: '',
    cover_letter: '',
  });
  const [resume, setResume] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [alreadyApplied, setAlreadyApplied] = useState(false);
  const [verificationPending, setVerificationPending] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [useProfileResume, setUseProfileResume] = useState(false);
  const feedbackRef = useRef(null);

  const { response: jobResponse, isLoading: isLoadingJob, error: jobError } = useFetch(process.env.REACT_APP_BASE_API_URL + `/jobs/${id}`);
  const { response: profileResponse } = useFetch(process.env.REACT_APP_BASE_API_URL + '/candidate-profile');

  const job = jobResponse?.job;
  const isExpired = Boolean(job?.is_expired);
  const canApply = job?.can_apply !== undefined ? Boolean(job.can_apply) : (job?.status === 'active' && !isExpired);
  const summaryItems = Array.from(new Set([
    job?.company_display_name ?? job?.company_name,
    job?.location,
    job?.job_type,
    job?.workplace_type,
  ].filter(Boolean)));
  const profile = profileResponse?.candidate_profile;
  const profileResumeName = profile?.resume_original_name || (profile?.resume_url ? 'Profile resume' : '');

  useEffect(() => {
    setAlreadyApplied(Boolean(jobResponse?.job?.has_applied));
  }, [jobResponse]);

  useEffect(() => {
    if (!profileResponse?.candidate_profile) return;

    const profile = profileResponse.candidate_profile;

    setForm((current) => ({
      ...current,
      name: profile.full_name ?? current.name,
      email: profile.email ?? current.email,
      phone: profile.phone ?? current.phone,
    }));
    setUseProfileResume(Boolean(profile.resume_url));
  }, [profileResponse]);

  useEffect(() => {
    if (!success && !error && validationErrors.length === 0) {
      return;
    }

    feedbackRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [success, error, validationErrors]);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((current) => ({ ...current, [name]: value }));
  }

  function handleFile(e) {
    const file = e.target.files?.[0] ?? null;

    setError(false);
    setValidationErrors([]);
    setFieldErrors({});
    setResume(file);
    if (file) setUseProfileResume(false);
  }

  function handleSubmit(e) {
    e.preventDefault();

    setIsSubmitting(true);
    setSuccess(false);
    setError(false);
    setValidationErrors([]);
    setFieldErrors({});

    let formData = new FormData();
    formData.append('name', form.name);
    formData.append('email', form.email);
    formData.append('phone', form.phone);
    formData.append('cover_letter', form.cover_letter);
    formData.append('use_profile_resume', useProfileResume && !resume ? '1' : '0');
    if (resume) formData.append('resume', resume);

    apiRequest(`/jobs/${id}/apply`, {
      method: 'POST',
      body: formData,
    })
      .then(data => {
        setIsSubmitting(false);

        if (data?.status === 'success') {
          setSuccess(data.message);
          setResume(null);
          setAlreadyApplied(true);
          setVerificationPending(Boolean(data?.email_verification_required || data?.application?.submission_status === "pending_verification"));
        }
        else if (data?.status === 'validation_error') {
          setValidationErrors(data.messages);
          setFieldErrors(data.errors ?? {});
        }
        else if (data?.status === 'error' && data?.message) {
          setError(data.message);
        }
        else {
          setError('There was some error while submitting application, please try again.');
        }
      })
      .catch(error => {
        setIsSubmitting(false);
        if (error.data?.status === 'validation_error') {
          setValidationErrors(error.messages);
          setFieldErrors(error.errors);
          if (error.errors?.job_id?.length > 0) {
            setAlreadyApplied(true);
          }
        }
        else {
          setError(error.message);
        }
      });
  }

  return (
    <section className="user-dashboard apply-job-page">
      <div className="auto-container">
        <div className="inner-column">
          <div className="upper-title-box">
            <h3>Apply for: {job?.title || 'Job'}</h3>
            <div className="text">Submit your application to the employer who posted this job.</div>
          </div>

          <div className="row">
            <div className="content-column col-lg-8 col-md-12 col-sm-12">
              <div className="ls-widget cv-manager-widget">
                <div className="widget-title">
                  <h4>{alreadyApplied ? 'Already Applied' : 'Apply Now'}</h4>
                </div>
                <div className="widget-content">
                  {isLoadingJob && <h5 className="text-center my-3">Loading Data</h5>}
                  {jobError && <FormFeedback error={jobError} />}
                  <div ref={feedbackRef}>
                    <FormFeedback success={success} error={error} validationErrors={validationErrors} />
                  </div>

                  {!isLoadingJob && !jobError && !job && (
                    <EmptyState
                      title="Job not found"
                      text="This job may have been removed or is no longer available."
                      actionText="Browse Jobs"
                      actionTo="/jobs"
                    />
                  )}

                  {!isLoadingJob && !jobError && job && !canApply && (
                    <div className="employee-empty-state">
                      {isExpired ? 'This job has expired and is no longer accepting applications.' : 'This job is not accepting applications right now.'}
                      <Link to={`/jobs/${id}`} className="theme-btn btn-style-three" style={{ marginLeft: 12 }}>
                        Back to Job
                      </Link>
                    </div>
                  )}

                  {!isLoadingJob && !jobError && job && canApply && alreadyApplied && (
                    <div className="employee-empty-state">
                      {verificationPending
                        ? 'Your application is saved and waiting for email verification before it is shared with the employer.'
                        : 'You have already applied for this job.'}
                      <Link to="/candidate-applied-jobs" className="theme-btn btn-style-three" style={{ marginLeft: 12 }}>
                        View Applications
                      </Link>
                    </div>
                  )}

                  {!isLoadingJob && !jobError && job && canApply && !alreadyApplied && (
                    <form className="default-form" onSubmit={handleSubmit}>
                      <div className="row clearfix">
                        <div className="form-group col-lg-6 col-md-6 col-sm-12">
                          <label>Full name</label>
                      <input type="text" name="name" className={inputErrorClass(fieldErrors, 'name')} placeholder="Your full name" value={form.name} onChange={handleChange} required />
                      <FieldError errors={fieldErrors} name="name" />
                        </div>

                        <div className="form-group col-lg-6 col-md-6 col-sm-12">
                          <label>Email</label>
                      <input type="email" name="email" className={inputErrorClass(fieldErrors, 'email')} placeholder="you@example.com" value={form.email} onChange={handleChange} required />
                      <FieldError errors={fieldErrors} name="email" />
                        </div>

                        <div className="form-group col-lg-6 col-md-6 col-sm-12">
                          <label>Phone</label>
                      <input type="text" name="phone" className={inputErrorClass(fieldErrors, 'phone')} placeholder="Phone number" value={form.phone} onChange={handleChange} />
                      <FieldError errors={fieldErrors} name="phone" />
                        </div>

                        <div className="form-group col-lg-12 col-md-12 col-sm-12">
                          <label>Cover Letter</label>
                      <textarea name="cover_letter" className={inputErrorClass(fieldErrors, 'cover_letter')} placeholder="Write a short cover letter" value={form.cover_letter} onChange={handleChange} rows={6} />
                      <FieldError errors={fieldErrors} name="cover_letter" />
                        </div>

                        <div className="form-group col-lg-12 col-md-12 col-sm-12">
                          <label>Upload Resume (PDF / DOC, max 5MB)</label>
                          {profile?.resume_url && (
                            <label className="application-profile-resume">
                              <input
                                type="checkbox"
                                checked={useProfileResume && !resume}
                                onChange={(e) => setUseProfileResume(e.target.checked)}
                                disabled={Boolean(resume)}
                              />
                              <span>Use resume from my candidate profile{profileResumeName ? ` (${profileResumeName})` : ''}</span>
                            </label>
                          )}
                          <div className="uploading-resume">
                            <div className="uploadButton">
                              <input className="uploadButton-input" type="file" accept=".pdf,.doc,.docx" id="upload" onChange={handleFile} />
                              <label className="cv-uploadButton" htmlFor="upload">
                                <span className="title">{resume ? resume.name : useProfileResume && profileResumeName ? profileResumeName : 'Drop files here to upload'}</span>
                                <span className="text">Maximum 5MB. Allowed: .doc .docx .pdf</span>
                                <span className="theme-btn btn-style-one btn-title">{resume ? 'Change Resume' : 'Upload Resume'}</span>
                              </label>
                      </div>
                      <FieldError errors={fieldErrors} name="resume" />
                    </div>
                        </div>

                        <div className="form-group col-lg-12 col-md-12 col-sm-12">
                          <button className="theme-btn btn-style-one bg-blue" type="submit" disabled={isSubmitting}>
                            <span className="btn-title">{isSubmitting ? 'Loading...' : 'Submit Application'}</span>
                          </button>
                          <Link to={`/jobs/${id}`} className="theme-btn btn-style-three" style={{ marginLeft: 12 }}>
                            Back to Job
                          </Link>
                        </div>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>

            <div className="sidebar-column col-lg-4 col-md-12 col-sm-12">
              <aside className="sidebar">
                <div className="sidebar-widget">
                  <h4 className="widget-title">Job Summary</h4>
                  <div className="widget-content">
                    <ul className="list-style-two">
                      {summaryItems.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
